import { makeAutoObservable } from 'mobx';
import { SubscriberDomainParams } from 'global/hooks/useSubscriberDomainParams';
import GroupModule from 'store/modules/group/groupModule';
import { RouteV2Id } from 'routes/routesConfigV2';
import { redirectToRoute } from 'routes/redirectToRoute';
import { EmployeeFlowGroupInfoDelegate } from './employeeFlowGroupInfoDelegate';

export type EmployeeFlowDomainParams = SubscriberDomainParams;

export interface EmployeeFlowConstructorParams {
  domainParams: SubscriberDomainParams;
  /**
   * When omitted, completion redirects to the Benefits Dashboard (`/employee`).
   */
  nextPageId?: RouteV2Id;
}

export default class EmployeeFlowDelegate {
  private _domainData: SubscriberDomainParams;
  private _groupModule: GroupModule;
  private _isInitialDataLoading = false;
  private _isActionLoading = false;
  public groupInfoDelegate: EmployeeFlowGroupInfoDelegate;
  private _nextPageId: RouteV2Id | null;

  public get domainData(): SubscriberDomainParams {
    return this._domainData;
  }

  public get groupModule(): GroupModule {
    return this._groupModule;
  }

  public get nextPageId(): RouteV2Id | null {
    return this._nextPageId;
  }

  /** Used for the separate layout components: group info blob, etc. */
  public get isFlowDataLoading(): boolean {
    return this._groupModule.isLoading;
  }

  /** Used for the initial page content loader */
  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading || this.isFlowDataLoading;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public setIsActionLoading(isActionLoading: boolean): void {
    this._isActionLoading = isActionLoading;
  }

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  public async getFlowInitialData(): Promise<void> {
    await this._groupModule.getGroup(this._domainData.groupId);
  }

  public onCompleteRedirect(): void {
    if (this._nextPageId) {
      redirectToRoute(this._nextPageId, { ...this._domainData });
      return;
    }

    redirectToRoute(RouteV2Id.EMPLOYEE_PROFILE, { groupId: this._domainData.groupId });
  }

  constructor({ domainParams, nextPageId }: EmployeeFlowConstructorParams) {
    this._domainData = domainParams;
    this._groupModule = new GroupModule();
    this.groupInfoDelegate = new EmployeeFlowGroupInfoDelegate(this._groupModule);
    this._nextPageId = nextPageId ?? null;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
